<div class="sb">
    <!-- ───────────────────────── SETUP ───────────────────────── -->
    <ng-container *ngIf="stage === 'setup'">
        <div class="sb__head">
            <div>
                <div class="sb__eyebrow">
                    <i class="fa-solid fa-wand-magic-sparkles"></i> Sequence builder
                </div>
                <h1 class="sb__title">{{ headerTitle }}</h1>
                <p class="sb__subtitle">
                    Tell us who you want to reach. We'll draft a cold-email sequence you can review,
                    tweak, and push to Reply — paused.
                </p>
            </div>
        </div>

        <div class="sb__setup-card">
            <div class="sb__form-field">
                <label class="sb__form-label" for="sb-persona">Audience name</label>
                <input
                    id="sb-persona"
                    class="sb__text-input"
                    type="text"
                    [(ngModel)]="personaName"
                    placeholder="e.g. Membership Director"
                    aria-label="Audience name" />
            </div>

            <div class="sb__form-field">
                <label class="sb__form-label" for="sb-criteria">Who are they?</label>
                <textarea
                    id="sb-criteria"
                    class="sb__textarea"
                    [(ngModel)]="personaCriteria"
                    rows="3"
                    placeholder="What they care about, what problem you solve for them, how you found them."
                    aria-label="Persona criteria"></textarea>
            </div>

            <div class="sb__form-field">
                <label class="sb__form-label" for="sb-touches">How many emails?</label>
                <select id="sb-touches" class="sb__select" [(ngModel)]="stepCount" aria-label="Number of touches">
                    <option *ngFor="let n of stepOptions" [ngValue]="n">{{ n }} email{{ n > 1 ? 's' : '' }}</option>
                </select>
            </div>

            <div class="sb__setup-actions">
                <button
                    type="button"
                    class="sb__btn sb__btn--primary"
                    [disabled]="!setupValid || isGenerating"
                    (click)="onGenerate()">
                    <i class="fa-solid fa-wand-magic-sparkles"></i> Generate draft
                </button>
            </div>

            <div class="sb__alert sb__alert--error" *ngIf="errorMessage && !notDeployed">
                <i class="fa-solid fa-circle-exclamation"></i> {{ errorMessage }}
            </div>
            <div class="sb__alert sb__alert--info" *ngIf="notDeployed">
                <i class="fa-solid fa-circle-info"></i>
                The sequence builder isn't available in this environment yet. {{ errorMessage }}
            </div>
        </div>
    </ng-container>

    <!-- ───────────────────────── GENERATING ───────────────────────── -->
    <ng-container *ngIf="stage === 'generating'">
        <div class="sb__loading">
            <div class="sb__spinner" aria-hidden="true"></div>
            <div class="sb__loading-title">Drafting your sequence…</div>
            <div class="sb__loading-sub">Writing {{ stepCount }} email{{ stepCount > 1 ? 's' : '' }} for {{ personaName }}.</div>
        </div>
    </ng-container>

    <!-- ───────────────────────── CREATING ───────────────────────── -->
    <ng-container *ngIf="stage === 'creating'">
        <div class="sb__loading">
            <div class="sb__spinner" aria-hidden="true"></div>
            <div class="sb__loading-title">Creating in Reply (paused)…</div>
            <div class="sb__loading-sub">Nothing sends — we're staging the sequence for review.</div>
        </div>
    </ng-container>

    <!-- ───────────────────────── REFINE ───────────────────────── -->
    <ng-container *ngIf="stage === 'refine' || stage === 'created'">
        <div class="sb__head">
            <div>
                <div class="sb__eyebrow">
                    <i class="fa-solid fa-wand-magic-sparkles"></i> Sequence builder
                </div>
                <h1 class="sb__title">{{ headerTitle }}</h1>
                <p class="sb__subtitle">Review the draft, tweak anything that's flagged, then push it to Reply — paused.</p>
            </div>
            <div class="sb__steps">
                <div class="sb__step is-done"><span class="sb__step-num"><i class="fa-solid fa-check"></i></span> Set up</div>
                <span class="sb__step-sep"></span>
                <div class="sb__step is-done"><span class="sb__step-num"><i class="fa-solid fa-check"></i></span> Draft</div>
                <span class="sb__step-sep"></span>
                <div class="sb__step is-active"><span class="sb__step-num">3</span> Refine</div>
                <span class="sb__step-sep"></span>
                <div class="sb__step"><span class="sb__step-num">4</span> Push</div>
            </div>
        </div>

        <!-- Setup summary -->
        <div class="sb__setup">
            <div class="sb__setup-field" *ngIf="brandName">
                <span class="sb__setup-label">Brand</span>
                <span class="sb__setup-value">{{ brandName }}</span>
            </div>
            <div class="sb__setup-sep" *ngIf="brandName"></div>
            <div class="sb__setup-field">
                <span class="sb__setup-label">Audience</span>
                <span class="sb__setup-value">
                    {{ personaName }}
                    <a class="sb__link" (click)="onChangeSetup()">change</a>
                </span>
            </div>
            <div class="sb__setup-sep"></div>
            <div class="sb__setup-field">
                <span class="sb__setup-label">Touches</span>
                <span class="sb__setup-value">{{ cards.length }} email{{ cards.length > 1 ? 's' : '' }}</span>
            </div>
            <button type="button" class="sb__btn sb__btn--ghost sb__regen-all" [disabled]="isGenerating" (click)="onRegenerateAll()">
                <i class="fa-solid fa-rotate"></i> Regenerate all
            </button>
        </div>

        <div class="sb__alert sb__alert--error" *ngIf="errorMessage && !notDeployed">
            <i class="fa-solid fa-circle-exclamation"></i> {{ errorMessage }}
        </div>
        <div class="sb__alert sb__alert--info" *ngIf="notDeployed">
            <i class="fa-solid fa-circle-info"></i> {{ errorMessage }}
        </div>

        <!-- Working surface -->
        <div class="sb__work">
            <div class="sb__cards">
                <div class="sb__card" *ngFor="let card of cards; trackBy: trackCard">
                    <div class="sb__card-head">
                        <span class="sb__touch-no">EMAIL {{ card.touchNumber }}</span>
                        <span class="sb__delay"><i class="fa-regular fa-clock"></i> {{ card.delayLabel }}</span>
                        <span class="sb__card-status" [class.is-ok]="card.findingCount === 0" [class.is-flag]="card.findingCount > 0">
                            <ng-container *ngIf="card.findingCount === 0">
                                <i class="fa-solid fa-check"></i> Looks good
                            </ng-container>
                            <ng-container *ngIf="card.findingCount > 0">
                                <i class="fa-solid fa-triangle-exclamation"></i> {{ card.findingCount }} to look at
                            </ng-container>
                        </span>
                    </div>
                    <div class="sb__card-body">
                        <div>
                            <div class="sb__field-label">Subject options</div>
                            <div class="sb__subjects">
                                <div class="sb__subject" *ngFor="let subject of card.subjects; let j = index">
                                    <span class="sb__subject-tag">{{ subject.tag }}</span>
                                    <div class="sb__subject-editor">
                                        <div class="sb__subject-backdrop" aria-hidden="true" [innerHTML]="subject.highlightHtml"></div>
                                        <input
                                            type="text"
                                            class="sb__subject-input"
                                            spellcheck="false"
                                            [(ngModel)]="subject.value"
                                            (ngModelChange)="onEdit()"
                                            aria-label="Subject option" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div class="sb__field-label">Body</div>
                            <div class="sb__editor">
                                <div #backdrop class="sb__backdrop" aria-hidden="true" [innerHTML]="card.bodyHighlightHtml"></div>
                                <textarea
                                    #bodyArea
                                    class="sb__input"
                                    spellcheck="false"
                                    [(ngModel)]="card.body"
                                    (ngModelChange)="onEdit()"
                                    (scroll)="onBodyScroll(card, bodyArea, backdrop)"
                                    aria-label="Email body"></textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Voice check rail -->
            <aside class="sb__rail">
                <div class="sb__rail-card">
                    <div class="sb__rail-head">
                        <i class="fa-solid fa-shield-halved"></i>
                        <h3>Voice check</h3>
                        <span class="sb__live">
                            <span class="sb__live-dot" [class.is-busy]="isLinting"></span>
                            {{ isLinting ? 'checking' : 'live' }}
                        </span>
                    </div>

                    <div class="sb__verdict" [class.is-ok]="verdictIsOk" [class.is-flag]="!verdictIsOk">
                        <div class="sb__verdict-ring">
                            <i class="fa-solid" [class.fa-check]="verdictIsOk" [class.fa-triangle-exclamation]="!verdictIsOk"></i>
                        </div>
                        <div>
                            <div class="sb__verdict-title">{{ verdictTitle }}</div>
                            <div class="sb__verdict-sub">{{ verdictSubtitle }}</div>
                        </div>
                    </div>

                    <div class="sb__findings">
                        <div class="sb__empty" *ngIf="railFindings.length === 0">
                            Edit the copy and this checks itself as you type.
                        </div>
                        <div class="sb__finding" *ngFor="let rf of railFindings; trackBy: trackRail">
                            <div class="sb__finding-top">
                                <span class="sb__sev" [ngClass]="rf.chipClass">{{ railLabelText(rf) }}</span>
                                <span class="sb__finding-where">{{ railWhere(rf.finding) }}</span>
                            </div>
                            <div class="sb__finding-msg">{{ rf.finding.message }}</div>
                            <span class="sb__finding-snippet" *ngIf="rf.finding.match">"{{ rf.finding.match }}"</span>
                        </div>
                    </div>
                </div>
            </aside>
        </div>

        <!-- Sticky action bar -->
        <div class="sb__actionbar">
            <div class="sb__actionbar-inner">
                <div class="sb__safe">
                    <i class="fa-solid fa-shield-halved"></i>
                    <span><strong>Nothing sends yet.</strong> This is created paused in Reply until a person starts it.</span>
                </div>
                <div class="sb__actions">
                    <button type="button" class="sb__btn sb__btn--ghost" (click)="onDiscard()">Discard</button>
                    <button type="button" class="sb__btn sb__btn--primary" [disabled]="isCreating || cards.length === 0" (click)="onCreate()">
                        <i class="fa-solid fa-pause"></i> Create in Reply (paused)
                    </button>
                </div>
            </div>
        </div>
    </ng-container>

    <!-- ───────────────────────── PAUSED CONFIRMATION ───────────────────────── -->
    <div class="sb__overlay" [class.is-open]="stage === 'created'" *ngIf="stage === 'created'">
        <div class="sb__modal">
            <div class="sb__modal-ring"><i class="fa-solid fa-pause"></i></div>
            <div class="sb__paused-pill"><i class="fa-solid fa-pause"></i> Paused in Reply</div>
            <h2>Sequence created — nothing has sent</h2>
            <p>{{ confirmationBody }}</p>
            <div class="sb__modal-warnings" *ngIf="createWarnings.length > 0">
                <div class="sb__modal-warnings-title">{{ createWarnings.length }} note{{ createWarnings.length > 1 ? 's' : '' }} from Reply</div>
                <ul>
                    <li *ngFor="let w of createWarnings">{{ w }}</li>
                </ul>
            </div>
            <div class="sb__modal-row">
                <button type="button" class="sb__btn sb__btn--ghost" (click)="onCloseModal()">Back to builder</button>
                <a
                    class="sb__btn sb__btn--primary"
                    *ngIf="createdDashboardUrl"
                    [href]="createdDashboardUrl"
                    target="_blank"
                    rel="noopener noreferrer">Open in Reply</a>
            </div>
        </div>
    </div>
</div>
